// TODO: This input has default AntDesign styles yet
// TODO: Missing Checkbox.Group feature

import type { TExample } from '@atlas/uicorn/preview/modules';
import React from 'react';
import styled from 'styled-components';
import { Checkbox as AntCheckbox, CheckboxProps as TAntCheckboxProps } from 'antd';
import type { CheckboxChangeEvent as TAntCheckboxChangeEvent } from 'antd/lib/checkbox';

const Checkbox = React.memo(
    React.forwardRef<TCheckboxRef, TCheckboxProps>(function Checkbox(props, ref): React.ReactElement {
        const { checked, ...checkboxProps } = props;
        return <SCheckbox {...checkboxProps} {...(typeof checked === 'boolean' && { checked })} ref={ref} />;
    }),
);

export default Checkbox;

// #region Styles

const SCheckbox = styled(AntCheckbox)`
    --atlas-checkbox-border-radius: 0.5rem;

    & {
        font: ${(p) => p.theme.font.input};
        align-items: flex-start;
    }

    .ant-checkbox {
        top: 0;
        font: inherit;
    }

    .ant-checkbox-inner {
        width: 1.8rem;
        height: 1.8rem;
        border: 0.1rem solid ${(p) => p.theme.color.grey6};
        background-color: ${(p) => p.theme.color.grey0};
        border-radius: var(--atlas-checkbox-border-radius);
    }

    .ant-checkbox-checked,
    .ant-checkbox-indeterminate {
        &::after {
            border-radius: var(--atlas-checkbox-border-radius);
            border: 0.1rem solid ${(p) => p.theme.color.primary};
        }

        .ant-checkbox-inner {
            background-color: ${(p) => p.theme.color.primary};
            border-color: transparent;
        }

        &.ant-checkbox-disabled .ant-checkbox-inner {
            background-color: ${(p) => p.theme.color.grey6};
            border: 0.1rem solid ${(p) => p.theme.color.grey6} !important;
        }
    }

    .ant-checkbox-checked .ant-checkbox-inner::after {
        border-width: 0.2rem;
        border-color: ${(p) => p.theme.color.grey0};
    }

    .ant-checkbox-indeterminate .ant-checkbox-inner::after {
        width: 1.1rem;
        height: 0.2rem;
        background-color: ${(p) => p.theme.color.grey0};
        border-radius: 0.1rem;
        left: 50%;
    }

    .ant-checkbox-inner::after {
        width: 0.571428571rem;
        height: 0.914285714rem;
        left: 23.5%;
    }

    .ant-checkbox + span {
        padding: 0 0.6rem;
        line-height: 1.4;
        color: ${(p) => p.theme.color.grey9};
    }

    .ant-checkbox-inner {
        outline: 0.1rem solid transparent;
        outline-offset: -0.1rem;
    }

    &.ant-checkbox-wrapper:not(.ant-checkbox-wrapper-disabled):hover .ant-checkbox-inner,
    .ant-checkbox:not(.ant-checkbox-disabled):hover .ant-checkbox-inner,
    .ant-checkbox-input:focus + .ant-checkbox-inner {
        outline-color: ${(p) => p.theme.color.primary};
        outline-offset: 0rem;
    }
`;

// #endregion

// #region Types

export type TCheckboxChangeEvent = TAntCheckboxChangeEvent;

export type TCheckboxProps = Pick<
    TAntCheckboxProps,
    | 'className'
    | 'children'
    | 'checked'
    | 'defaultChecked'
    | 'indeterminate'
    | 'disabled'
    | 'tabIndex'
    | 'onChange'
    | 'onClick'
>;

export type TCheckboxRef = HTMLInputElement;

// #endregion

// #region Examples

export const Example: TExample = {
    render(props) {
        return (
            <Checkbox
                children={props.children || undefined}
                checked={props.checked}
                indeterminate={props.indeterminate}
                disabled={props.disabled}
                onChange={props.onClick}
            />
        );
    },
    controls: {
        children: { type: 'string', initial: '' },
        checked: { type: 'boolean', initial: true, connected: false },
        indeterminate: 'boolean',
        disabled: 'boolean',
    },
    handlers: {
        onClick: 'console',
    },
    figma: 'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System----2.0?node-id=6:528&mode=dev',
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;
    describe('Checkbox', async () => {
        const testModule = '@test';
        const { render } = await import(/* @vite-ignore */ testModule);

        it('matches snapshot', () => {
            const result = render(<Checkbox>Press</Checkbox>);
            expect(result.container).toMatchSnapshot();
        });
    });
}

// #endregion
